import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

/** 削除対象。タグ種別なら配下のタグ名の件数も出す */
export interface DeleteTarget {
  kind: 'type' | 'value';
  id: string;
  name: string;
  /** タグ種別のみ。配下のタグ名の件数 */
  valueCount?: number;
  /** 付与されている店舗数 */
  sellerCount: number;
}

interface DeleteTagDialogProps {
  target: DeleteTarget | null;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (target: DeleteTarget) => void;
}

/**
 * タグ種別・タグ名の削除確認。
 *
 * 何がどれだけ消えるのかを件数で見せてから消させる。付与ごと消えるため、
 * 件数を見ずに消すと「どの店舗に付いていたか」が分からなくなる。
 */
export function DeleteTagDialog({ target, submitting, onCancel, onConfirm }: DeleteTagDialogProps) {
  if (!target) return null;

  const isType = target.kind === 'type';
  const inUse = target.sellerCount > 0 || (target.valueCount ?? 0) > 0;

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isType ? 'タグ種別を削除' : 'タグ名を削除'}</DialogTitle>
          <DialogDescription>「{target.name}」を削除します。</DialogDescription>
        </DialogHeader>

        {inUse ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
            <p className="font-medium">削除すると、次のものも一緒に消えます</p>
            <ul className="mt-1.5 list-inside list-disc text-xs">
              {isType && target.valueCount ? <li>配下のタグ名 {target.valueCount}件</li> : null}
              {target.sellerCount > 0 ? <li>{target.sellerCount}店舗への付与</li> : null}
            </ul>
            <p className="mt-1.5 text-xs">この操作は元に戻せません。</p>
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            どの店舗にも付いていないため、そのまま削除できます。
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            キャンセル
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={submitting}
            onClick={() => onConfirm(target)}
          >
            削除
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
